import React, { useEffect, useMemo, useState } from 'react'
import { collection, onSnapshot, query, where, orderBy, limit, updateDoc, doc } from 'firebase/firestore'
import { getAuth } from 'firebase/auth'
import { db } from '../firebase-config'
import { sanitizeDni } from '../utils/authHelper'

const ORACLE_APPROVE_URL = import.meta.env.VITE_ORACLE_APPROVE_URL || ''
const MOTOR_HASH = import.meta.env.VITE_MOTOR_HASH || 'v1.3.1'

function obtenerAdminIdSesion() {
  const user = getAuth().currentUser
  if (!user) return 'desconocido'
  if (user.email) {
    const local = user.email.split('@')[0] || ''
    const dni = sanitizeDni(local)
    if (dni) return dni
  }
  return user.uid || 'desconocido'
}

export default function AlertasNormativas({ alertas = null }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (Array.isArray(alertas)) {
      setItems(alertas)
      setLoading(false)
      return undefined
    }

    const q = query(
      collection(db, 'propuestas_normativas'),
      where('estado', '==', 'pendiente_aprobacion'),
      orderBy('creadoEn', 'desc'),
      limit(20)
    )

    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
      setItems(rows)
      setLoading(false)
    }, () => {
      setLoading(false)
    })

    return () => unsub()
  }, [alertas])

  const lista = useMemo(() => Array.isArray(items) ? items : [], [items])
  if (loading || lista.length === 0) return null

  const handleRechazar = async (id) => {
    await updateDoc(doc(db, 'propuestas_normativas', id), {
      estado: 'rechazado',
      rechazadoEn: new Date().toISOString(),
    })
  }

  const handleAprobar = async (id) => {
    if (!ORACLE_APPROVE_URL) {
      throw new Error('Falta VITE_ORACLE_APPROVE_URL para aprobar propuestas')
    }
    const resp = await fetch(ORACLE_APPROVE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        propuestaId: id,
        adminId: obtenerAdminIdSesion(),
        hashMotor: MOTOR_HASH,
      }),
    })
    if (!resp.ok) {
      throw new Error('No se pudo aplicar la propuesta normativa')
    }
  }

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
      <h3 className="mb-3 text-sm font-semibold text-amber-900">Actualizaciones Detectadas (Fuentes Verificadas)</h3>
      <div className="space-y-2">
        {lista.map((alerta) => (
          <div key={alerta.id} className="rounded-md border border-amber-200 bg-white p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${alerta.confianza === 'alta' ? 'bg-green-100 text-green-700' : alerta.confianza === 'media' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                Confianza: {alerta.confianza || 'baja'}
              </span>
              {alerta.fuenteUrl && (
                <a href={alerta.fuenteUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-700 underline">
                  Ver fuente oficial
                </a>
              )}
            </div>
            <p className="text-sm text-gray-800"><strong>{alerta.entidad}:</strong> {alerta.titulo}</p>
            <p className="mt-1 text-xs text-gray-600">Impacto: {alerta.descripcionImpacto}</p>
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={() => handleRechazar(alerta.id)}
                className="rounded border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-100"
              >
                Descartar
              </button>
              <button
                type="button"
                onClick={() => handleAprobar(alerta.id)}
                className="rounded bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-700"
              >
                Aplicar al Motor v1.2
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
